import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';
import { Country, Language, LocationDocumentType } from 'src/types/enums';

import type { LocationsResponse } from './endpoints/LocationsEndpoint.types';
import type { LocationByIdResponse, LocationDetail } from './endpoints/LocationEndpoint.types';
import type { PostalCode, PostalCodesResponse } from './endpoints/PostalCodesEndpoint.types';
import type { CreateLocationResponse } from './endpoints/CreateLocationEndpoint.types';
import type { UpdateLocationResponse } from './endpoints/UpdateLocationEndpoint.types';
import type { CreateLocationContactResponse } from './endpoints/CreateLocationContactEndpoint.types';
import type { UpdateLocationContactResponse } from './endpoints/UpdateLocationContactEndpoint.types';
import type { DeleteLocationContactsResponse } from './endpoints/DeleteLocationContactsEndpoint.types';
import type { UpdateOpeningHoursItem, UpdateOpeningHoursResponse } from './endpoints/UpdateOpeningHoursEndpoint.types';
import type { LinkLocationDocumentResponse } from './endpoints/LinkLocationDocumentEndpoint.types';
import type { UnlinkLocationDocumentResponse } from './endpoints/UnlinkLocationDocumentEndpoint.types';

// Cleaner re-exports for consumers.
export type Location = LocationDetail;
export type LocationPostalCode = PostalCode;

export const LOCATIONS_SWR_KEY = 'locations';
export const LOCATION_SWR_KEY = 'location';

// ─── Queries ─────────────────────────────────────────────────────────

export interface LocationsFilters {
  search?: string;
  onlyActive?: boolean;
}

export async function getLocations(
  cursor?: number | null,
  take?: number,
  filters?: LocationsFilters,
): Promise<LocationsResponse> {
  const params = new URLSearchParams();
  if (cursor != null) params.set('id', cursor.toString());
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (filters?.onlyActive) params.set('onlyActive', 'true');

  const queryString = params.toString();
  const url = queryString ? `/api/v1/locations?${queryString}` : '/api/v1/locations';
  return fetchEndpointData<LocationsResponse>(url);
}

export async function getLocationById(id: number): Promise<LocationByIdResponse> {
  return fetchEndpointData<LocationByIdResponse>(`/api/v1/locations/${id}`);
}

export async function findPostalCodes(query: string, country: string): Promise<PostalCodesResponse> {
  const params = new URLSearchParams({ query, country });
  return fetchEndpointData<PostalCodesResponse>(`/api/v1/locations/postal-codes?${params.toString()}`);
}

// ─── Query hooks ─────────────────────────────────────────────────────

// Mirrors LocationCombobox's `useGetLocationsQuery` (context suspense: false) —
// non-suspense, reads `data` defensively.
export function useLocations(filters: LocationsFilters) {
  return useSWR([LOCATIONS_SWR_KEY, filters.search, filters.onlyActive], () =>
    getLocations(undefined, undefined, filters),
  );
}

export function useLocationById(id: number) {
  return useSWR([LOCATION_SWR_KEY, id], () => getLocationById(id));
}

// ─── Mutations (fetchers throw on error) ─────────────────────────────

export interface CreateLocationInput {
  name: string;
  street: string;
  streetNumber: string;
  city: string;
  postalCode: string;
  country: Country;
  language: Language;
  internalNotes: string;
  externalNotes: string;
  isExternalBooking: boolean;
}

export async function createLocation(data: CreateLocationInput): Promise<CreateLocationResponse> {
  return fetchEndpointData<CreateLocationResponse>('/api/v1/locations', {
    method: 'POST',
    body: data,
  });
}

export interface UpdateLocationInput {
  name?: string;
  street?: string;
  streetNumber?: string;
  city?: string;
  postalCode?: string;
  country?: Country;
  language?: Language;
  deactivationReason?: string | null;
  internalNotes?: string;
  externalNotes?: string;
  isExternalBooking?: boolean;
}

export async function updateLocation(id: number, data: UpdateLocationInput): Promise<UpdateLocationResponse> {
  return fetchEndpointData<UpdateLocationResponse>(`/api/v1/locations/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

export interface CreateLocationContactInput {
  name: string;
  email: string;
  phone: string;
  function: string;
  language: Language;
  locationId: number;
  internalNotes: string;
}

export async function createLocationContact(data: CreateLocationContactInput): Promise<CreateLocationContactResponse> {
  return fetchEndpointData<CreateLocationContactResponse>('/api/v1/locations/contacts', {
    method: 'POST',
    body: data,
  });
}

export interface UpdateLocationContactInput {
  name?: string;
  email?: string;
  phone?: string;
  function?: string;
  language?: Language;
  internalNotes?: string;
}

export async function updateLocationContact(
  id: number,
  data: UpdateLocationContactInput,
): Promise<UpdateLocationContactResponse> {
  return fetchEndpointData<UpdateLocationContactResponse>(`/api/v1/locations/contacts/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function deleteLocationContacts(ids: number[]): Promise<DeleteLocationContactsResponse> {
  return fetchEndpointData<DeleteLocationContactsResponse>('/api/v1/locations/contacts', {
    method: 'DELETE',
    body: { ids },
  });
}

export async function updateOpeningHours(
  id: number,
  data: UpdateOpeningHoursItem[],
): Promise<UpdateOpeningHoursResponse> {
  return fetchEndpointData<UpdateOpeningHoursResponse>(`/api/v1/locations/${id}/opening-times`, {
    method: 'PATCH',
    body: { data },
  });
}

export async function linkLocationDocument(
  locationId: number,
  documentId: string,
  type: LocationDocumentType,
): Promise<LinkLocationDocumentResponse> {
  return fetchEndpointData<LinkLocationDocumentResponse>(`/api/v1/locations/${locationId}/documents`, {
    method: 'POST',
    body: { documentId, type },
  });
}

export async function unlinkLocationDocument(
  locationId: number,
  documentId: string,
  type: LocationDocumentType,
): Promise<UnlinkLocationDocumentResponse> {
  return fetchEndpointData<UnlinkLocationDocumentResponse>(`/api/v1/locations/${locationId}/documents`, {
    method: 'DELETE',
    body: { documentId, type },
  });
}
